import { useEffect } from "react";
import axios from "axios";
import Seo from "@/components/Seo";
import AdPlaceholder from "@/components/AdPlaceholder";
import RelatedCalcs from "@/components/RelatedCalcs";
import EditorBadge from "@/components/EditorBadge";
import Faq from "@/components/Faq";
import { getBySlug } from "@/data/calculators";
import { breadcrumbJsonLd } from "@/lib/seo";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared page shell for every calculator — keeps layout/ads/SEO/schema consistent.
// `hideHeader` lets pages (e.g. Geo variants) render their own breadcrumb + h1
// without getting a duplicate from the shell.
// `suppressSeo` skips the default <Seo> block so the page can set its own.
export default function CalculatorShell({ slug, children, explainer, hideHeader = false, suppressSeo = false }) {
    const meta = getBySlug(slug);

    useEffect(() => {
        // Fire-and-forget view tracking for the "Popular" widget
        axios.post(`${API}/tools/view`, { slug }).catch(() => {});
    }, [slug]);

    if (!meta) return null;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: meta.title,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            description: meta.description,
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Calculator", path: "/" },
            { name: meta.short, path: `/calculator/${slug}` },
        ]),
    ];

    return (
        <>
            {!suppressSeo && (
                <Seo
                    title={`${meta.seoTitle} | FinCalc`}
                    description={meta.description}
                    canonical={`/calculator/${slug}`}
                    jsonLd={jsonLd}
                    ogSlug={slug}
                />
            )}

            {!hideHeader && (
                <>
                    {/* Breadcrumb */}
                    <div className="container-editorial pt-8 pb-2">
                        <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                            <Link to="/" className="hover:text-primary">Home</Link>
                            <ChevronRight className="w-3 h-3" />
                            <span>Calculator</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-primary">{meta.short}</span>
                        </nav>
                    </div>

                    {/* Title */}
                    <section className="container-editorial pt-4 pb-8 anim-fade-up">
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                            {meta.title}
                        </h1>
                        <p className="mt-4 max-w-2xl text-lg text-foreground/70 leading-relaxed">
                            {meta.description}
                        </p>
                        <div className="mt-5">
                            <EditorBadge />
                        </div>
                    </section>
                </>
            )}

            {/* Leaderboard ad */}
            <div className="container-editorial mb-8">
                <AdPlaceholder type="leaderboard" dataTestId="ad-top-leaderboard" />
            </div>

            {/* Main grid: form+results | related sidebar */}
            <section className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 pb-12">
                <div className="lg:col-span-9">
                    {children}
                    <AdPlaceholder type="native" className="my-10" dataTestId="ad-in-content" />
                    {explainer && (
                        <article className="prose-editorial max-w-3xl">{explainer}</article>
                    )}
                    <Faq slug={slug} />
                </div>
                <aside className="lg:col-span-3 space-y-6">
                    <div className="lg:sticky lg:top-24 space-y-6">
                        <AdPlaceholder type="sidebar" dataTestId="ad-sidebar-top" />
                        <RelatedCalcs slug={slug} />
                    </div>
                </aside>
            </section>
        </>
    );
}
